import { DataSnapshot } from 'firebase/database';

import { DataBaseNodes } from '@lib/firebase';
import { useSiteStore } from '@stores';
import {
  ExpectedCareTaskAnswerObjectByBackend,
  SourceUnsubscribe,
} from '@type';
import { getDateUTCOffset, TIME_IN_MS } from '@utils';

import { Encryption } from '../../encryption-service';
import {
  CommunicationKeys,
  DecryptedTaskComponents,
  Preferences,
  TaskDetailsEncryptedResponse,
  TasksSummaryEncryptedResponse,
  TasksSummaryItemResponse,
} from '../types';
import { BaseGateway } from './base.gateway';

export class TaskGateway extends BaseGateway {
  getTasksSummary = async (siteUuid: string) => {
    const { getSiteByUUID } = useSiteStore.getState();
    const siteData = getSiteByUUID(siteUuid);

    const utcOffset = getDateUTCOffset();

    const payload = {
      deviceOffset: utcOffset,
      firebasePatientId: this.config.patientSourceUUID,
      preference: Preferences.CareTask.TasksSummary,
    };

    const response = await this.dataExchange<TasksSummaryEncryptedResponse>({
      successKey: CommunicationKeys.TasksSummary.Success,
      failureKey: CommunicationKeys.TasksSummary.Failure,
      siteUuid: siteData.siteUuid,
      payload,
    });

    if (!response?.document) {
      throw new Error(
        `Failed to get tasks summary siteUuid=${siteUuid} - No document found`
      );
    }

    const tasks = Encryption.decryptAndParseObject<TasksSummaryItemResponse[]>(
      response.document,
      siteData.sharedSecretKey
    );

    if (!tasks) {
      throw new Error(
        `Failed to get tasks summary siteUuid=${siteUuid} - No tasks decrypted`
      );
    }

    return tasks;
  };

  subscribeToTasksUpdateEvent = (
    callback: (snapshot: DataSnapshot) => void
  ): SourceUnsubscribe => {
    return this.sourceService.subscribeToChild(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.TasksUpdate}`,
      callback
    );
  };

  getTaskDetails = async (taskId: number, siteUuid: string) => {
    const { getSiteByUUID } = useSiteStore.getState();
    const siteData = getSiteByUUID(siteUuid);

    const payload = {
      preference: Preferences.CareTask.TaskDetails,
      careplanId: taskId,
      firebasePatientId: this.config.patientSourceUUID,
      siteId: siteData.siteId,
    };

    const response = await this.dataExchange<TaskDetailsEncryptedResponse>({
      successKey: CommunicationKeys.TaskDetails.Success,
      failureKey: CommunicationKeys.TaskDetails.Failure,
      siteUuid: siteData.siteUuid,
      payload,
    });

    if (!response?.careplan?.document) {
      throw new Error('Failed to get task details - No document found');
    }

    const decryptedTaskComponents =
      Encryption.decryptAndParseObject<DecryptedTaskComponents>(
        response.careplan.document,
        siteData.sharedSecretKey
      );

    if (!decryptedTaskComponents) {
      throw new Error(
        'Failed to get task details - No task components decrypted'
      );
    }

    return {
      careplan: response.careplan,
      decryptedTaskComponents,
    };
  };

  submitCareTaskAnswer = async (
    answerObject: ExpectedCareTaskAnswerObjectByBackend
  ) => {
    const { getSiteByProjectId } = useSiteStore.getState();
    const siteData = getSiteByProjectId(this.config.projectId!);

    const encryptedAnswerObject = Encryption.encrypt(
      answerObject,
      siteData.sharedSecretKey
    );

    const payload = {
      encrypted: true,
      hashedSharedKey: siteData.hashedSharedKey,
      message: encryptedAnswerObject,
    };

    return await this.dataExchange<{ occurrenceId: string }>(
      {
        siteUuid: siteData.siteUuid,
        payload: payload,
        query: DataBaseNodes.Responses,
        successKey: CommunicationKeys.Answer.Success,
        failureKey: CommunicationKeys.Answer.Failed,
        pushWithKey: false,
      },
      TIME_IN_MS.ONE_MINUTE
    ).catch(error => {
      throw new Error(
        `Error on submitting care task answer: ${JSON.stringify(error)}`
      );
    });
  };

  uploadAnswerFile = async (file: File) => {
    return this.sourceService.uploadFile(
      file,
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.Files}/`
    );
  };
}
